import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import GetAppIcon from "@material-ui/icons/GetApp";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  headerActions: { display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" },
  metric: { borderRadius: 16, height: "100%", border: `1px solid ${theme.palette.divider}`, boxShadow: "0 8px 24px rgba(0,70,110,.08)" },
  metricValue: { fontWeight: 800, color: theme.palette.primary.main, fontSize: 28 },
  filters: { padding: 16, borderRadius: 16, margin: "16px 0" },
  table: { borderRadius: 16, overflow: "hidden" },
  thumbnail: { width: 72, height: 72, objectFit: "cover", borderRadius: 12, background: "#eaf4f8" },
  textPreview: { width: 72, height: 72, borderRadius: 12, display: "grid", placeItems: "center", padding: 7, color: "white", textAlign: "center", fontWeight: 700, fontSize: 11, overflow: "hidden" },
  statusTitle: { fontWeight: 700 },
  uploadBox: { border: "2px dashed #83bfd9", borderRadius: 14, padding: 18, textAlign: "center", background: "rgba(74,169,207,.06)" },
  preview: { maxWidth: "100%", maxHeight: "65vh", borderRadius: 16, display: "block", margin: "0 auto" },
  phonePreview: { width: 280, minHeight: 500, margin: "0 auto", borderRadius: 24, padding: 18, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", color: "white", boxShadow: "0 16px 50px rgba(0,0,0,.24)", textAlign: "center" },
  viewsList: { maxHeight: 180, overflow: "auto", marginTop: 16, width: "100%" },
}));

const emptyForm = {
  title: "", text: "", whatsappId: "", backgroundColor: "#075E54", font: 0,
  scheduledAt: "", active: true, media: null,
};

const statusConfig = {
  draft: ["Borrador", "default"], scheduled: ["Programado", "primary"], publishing: ["Publicando", "secondary"],
  published: ["Publicado", "primary"], failed: ["Falló", "secondary"], disabled: ["Desactivado", "default"],
};

const formatDate = value => value ? new Intl.DateTimeFormat("es-CO", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";

const WhatsAppStatuses = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [records, setRecords] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [filters, setFilters] = useState({ search: "", status: "", dateFrom: "", dateTo: "" });
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp-statuses", { params: { ...filters, page: page + 1, limit: rowsPerPage } });
      setRecords(data.records || []);
      setCount(data.count || 0);
    } catch (error) { toastError(error); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (user?.profile !== "admin") return;
    api.get("/whatsapp/").then(({ data }) => setConnections(Array.isArray(data) ? data : [])).catch(toastError);
  }, [user]);

  useEffect(() => {
    if (user?.profile !== "admin") return;
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [user, page, rowsPerPage, filters.search, filters.status, filters.dateFrom, filters.dateTo]);

  const metrics = useMemo(() => ({
    total: count,
    scheduled: records.filter(item => item.status === "scheduled").length,
    published: records.filter(item => item.status === "published").length,
    views: records.reduce((sum, item) => sum + Number(item.viewerCount || 0), 0),
  }), [records, count]);

  if (user?.profile !== "admin") return <ForbiddenPage />;

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, whatsappId: connections.find(item => item.isDefault)?.id || connections[0]?.id || "" });
    setOpen(true);
  };

  const openEdit = record => {
    setEditing(record);
    setForm({
      title: record.title || "", text: record.text || "", whatsappId: record.whatsappId || "",
      backgroundColor: record.backgroundColor || "#075E54", font: record.font || 0,
      scheduledAt: record.scheduledAt ? new Date(new Date(record.scheduledAt).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "",
      active: record.active !== false, media: null,
    });
    setOpen(true);
  };

  const save = async publishNow => {
    if (!form.title.trim() || !form.whatsappId) return toast.error("Indique el título y la conexión de WhatsApp");
    if (!form.text.trim() && !form.media && !editing?.mediaPath) return toast.error("Escriba un texto o seleccione una imagen o video");
    const payload = new FormData();
    payload.append("typeArch", "whatsapp-statuses");
    payload.append("title", form.title);
    payload.append("text", form.text);
    payload.append("whatsappId", form.whatsappId);
    payload.append("backgroundColor", form.backgroundColor);
    payload.append("font", form.font);
    payload.append("scheduledAt", publishNow ? "" : form.scheduledAt);
    payload.append("publishNow", publishNow ? "true" : "false");
    payload.append("active", String(form.active));
    if (form.media) payload.append("media", form.media);
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/whatsapp-statuses/${editing.id}`, payload);
        if (publishNow) await api.post(`/whatsapp-statuses/${editing.id}/publish`);
      } else {
        await api.post("/whatsapp-statuses", payload);
      }
      toast.success(publishNow ? "Estado publicado" : (form.scheduledAt ? "Estado programado" : "Borrador guardado"));
      setOpen(false);
      await load();
    } catch (error) { toastError(error); }
    finally { setSaving(false); }
  };

  const publish = async record => {
    try { await api.post(`/whatsapp-statuses/${record.id}/publish`); toast.success("Estado publicado"); load(); }
    catch (error) { toastError(error); }
  };

  const toggle = async record => {
    try { await api.patch(`/whatsapp-statuses/${record.id}/toggle`); toast.success(record.active ? "Estado desactivado" : "Estado activado"); load(); }
    catch (error) { toastError(error); }
  };

  const remove = async record => {
    if (!window.confirm(`¿Eliminar el estado “${record.title}”?`)) return;
    try { await api.delete(`/whatsapp-statuses/${record.id}`); toast.success("Estado eliminado"); load(); }
    catch (error) { toastError(error); }
  };

  const exportCsv = () => {
    const headers = ["Título", "Tipo", "Conexión", "Estado", "Programado", "Publicado", "Audiencia", "Visualizaciones"];
    const rows = records.map(item => [item.title, item.contentType, item.whatsapp?.name || "", item.status, formatDate(item.scheduledAt), formatDate(item.publishedAt), item.audienceCount || 0, item.viewerCount || 0]);
    const csv = [headers, ...rows].map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    saveAs(new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }), `estados-whatsapp-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const renderThumb = record => record.mediaUrl
    ? (String(record.mimeType).startsWith("video/")
      ? <video className={classes.thumbnail} src={record.mediaUrl} muted />
      : <img className={classes.thumbnail} src={record.mediaUrl} alt={record.title} />)
    : <div className={classes.textPreview} style={{ background: record.backgroundColor || "#075E54" }}>{record.text}</div>;

  return (
    <MainContainer>
      <MainHeader>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={6}><Title>Estados WhatsApp</Title><Typography color="textSecondary">Cree, programe y supervise los estados de sus números conectados.</Typography></Grid>
          <Grid item xs={12} md={6} className={classes.headerActions}>
            <Button variant="outlined" startIcon={<GetAppIcon />} onClick={exportCsv}>Exportar</Button>
            <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={openNew}>Nuevo estado</Button>
          </Grid>
        </Grid>
      </MainHeader>

      <Grid container spacing={2}>
        {[['Total', metrics.total], ['Programados en esta página', metrics.scheduled], ['Publicados en esta página', metrics.published], ['Visualizaciones en esta página', metrics.views]].map(([label, value]) => (
          <Grid item xs={6} md={3} key={label}><Card className={classes.metric}><CardContent><Typography color="textSecondary">{label}</Typography><Typography className={classes.metricValue}>{value}</Typography></CardContent></Card></Grid>
        ))}
      </Grid>

      <Paper className={classes.filters} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}><TextField fullWidth label="Buscar por título o texto" value={filters.search} onChange={e => { setPage(0); setFilters({ ...filters, search: e.target.value }); }} /></Grid>
          <Grid item xs={12} sm={4} md={2}><FormControl fullWidth><InputLabel>Estado</InputLabel><Select value={filters.status} onChange={e => { setPage(0); setFilters({ ...filters, status: e.target.value }); }}><MenuItem value="">Todos</MenuItem>{Object.entries(statusConfig).map(([value, item]) => <MenuItem key={value} value={value}>{item[0]}</MenuItem>)}</Select></FormControl></Grid>
          <Grid item xs={6} sm={4} md={2}><TextField fullWidth type="date" label="Desde" InputLabelProps={{ shrink: true }} value={filters.dateFrom} onChange={e => { setPage(0); setFilters({ ...filters, dateFrom: e.target.value }); }} /></Grid>
          <Grid item xs={6} sm={4} md={2}><TextField fullWidth type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={filters.dateTo} onChange={e => { setPage(0); setFilters({ ...filters, dateTo: e.target.value }); }} /></Grid>
          <Grid item xs={12} md={2}><Button fullWidth onClick={() => setFilters({ search: "", status: "", dateFrom: "", dateTo: "" })}>Limpiar filtros</Button></Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} className={classes.table} variant="outlined">
        <Table>
          <TableHead><TableRow><TableCell>Vista</TableCell><TableCell>Estado</TableCell><TableCell>Conexión</TableCell><TableCell>Programación</TableCell><TableCell align="center">Alcance</TableCell><TableCell align="center">Acciones</TableCell></TableRow></TableHead>
          <TableBody>
            {!loading && records.length === 0 && <TableRow><TableCell colSpan={6} align="center"><Box py={6}><Typography variant="h6">Todavía no hay estados</Typography><Typography color="textSecondary">Cree el primero con el botón “Nuevo estado”.</Typography></Box></TableCell></TableRow>}
            {records.map(record => {
              const state = statusConfig[record.status] || [record.status, "default"];
              return <TableRow key={record.id} hover>
                <TableCell><Box display="flex" alignItems="center" gridGap={12}>{renderThumb(record)}<Box><Typography className={classes.statusTitle}>{record.title}</Typography><Typography variant="caption" color="textSecondary">{record.contentType.replace("_", " + ")}</Typography></Box></Box></TableCell>
                <TableCell><Chip size="small" label={state[0]} color={state[1]} /><Box mt={1}><FormControlLabel control={<Switch size="small" checked={record.active !== false} onChange={() => toggle(record)} color="primary" />} label={record.active !== false ? "Activo" : "Inactivo"} /></Box>{record.lastError && <Typography variant="caption" color="error">{record.lastError}</Typography>}</TableCell>
                <TableCell>{record.whatsapp?.name || `#${record.whatsappId}`}<Typography variant="caption" display="block" color="textSecondary">{record.whatsapp?.number || ""}</Typography></TableCell>
                <TableCell><Typography variant="body2">{record.scheduledAt ? `Programado: ${formatDate(record.scheduledAt)}` : "Sin programación"}</Typography><Typography variant="caption" color="textSecondary">{record.publishedAt ? `Publicado: ${formatDate(record.publishedAt)}` : `Creado: ${formatDate(record.createdAt)}`}</Typography></TableCell>
                <TableCell align="center"><Typography><b>{record.viewerCount || 0}</b> vistos</Typography><Typography variant="caption" color="textSecondary">de {record.audienceCount || 0} contactos</Typography></TableCell>
                <TableCell align="center">
                  <Tooltip title="Vista preliminar"><IconButton size="small" onClick={() => setPreview(record)}><VisibilityIcon /></IconButton></Tooltip>
                  {record.mediaUrl && <Tooltip title="Descargar"><IconButton size="small" component="a" href={record.mediaUrl} download target="_blank"><GetAppIcon /></IconButton></Tooltip>}
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(record)}><EditIcon /></IconButton></Tooltip>
                  <Tooltip title="Publicar ahora"><span><IconButton size="small" disabled={!record.active || record.status === "publishing"} onClick={() => publish(record)}><PlayArrowIcon /></IconButton></span></Tooltip>
                  <Tooltip title="Eliminar"><IconButton size="small" onClick={() => remove(record)}><DeleteOutlineIcon /></IconButton></Tooltip>
                </TableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>
        <TablePagination component="div" count={count} page={page} onPageChange={(_e, next) => setPage(next)} rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }} rowsPerPageOptions={[6, 12, 24, 48]} labelRowsPerPage="Por página" />
      </TableContainer>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Editar estado de WhatsApp" : "Nuevo estado de WhatsApp"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <TextField fullWidth margin="normal" label="Título interno" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} helperText="Solo lo verán los administradores." />
              <FormControl fullWidth margin="normal"><InputLabel>Conexión de WhatsApp</InputLabel><Select value={form.whatsappId} onChange={e => setForm({ ...form, whatsappId: e.target.value })}>{connections.filter(item => !item.channel || item.channel === "whatsapp").map(item => <MenuItem key={item.id} value={item.id}>{item.name} {item.number ? `· ${item.number}` : ""}</MenuItem>)}</Select></FormControl>
              <TextField fullWidth multiline minRows={4} margin="normal" label="Texto o descripción" value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} helperText="Puede publicar solo texto o combinarlo con una imagen o video." />
              <Grid container spacing={2}><Grid item xs={6}><TextField fullWidth type="color" label="Color de fondo" InputLabelProps={{ shrink: true }} value={form.backgroundColor} onChange={e => setForm({ ...form, backgroundColor: e.target.value })} /></Grid><Grid item xs={6}><FormControl fullWidth><InputLabel>Estilo de letra</InputLabel><Select value={form.font} onChange={e => setForm({ ...form, font: e.target.value })}><MenuItem value={0}>Clásica</MenuItem><MenuItem value={1}>Moderna</MenuItem><MenuItem value={2}>Elegante</MenuItem><MenuItem value={3}>Compacta</MenuItem></Select></FormControl></Grid></Grid>
              <TextField fullWidth margin="normal" type="datetime-local" label="Fecha y hora de publicación" InputLabelProps={{ shrink: true }} value={form.scheduledAt} onChange={e => setForm({ ...form, scheduledAt: e.target.value })} helperText="Déjelo vacío para guardarlo como borrador." />
              <FormControlLabel control={<Switch checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} color="primary" />} label="Estado activo" />
            </Grid>
            <Grid item xs={12} md={5}>
              <div className={classes.uploadBox}><CloudUploadIcon color="primary" fontSize="large" /><Typography variant="subtitle1">Imagen o video</Typography><Typography variant="caption" color="textSecondary" display="block">Imágenes JPG, PNG o WebP; videos compatibles con WhatsApp.</Typography><Box mt={2}><Button component="label" variant="outlined" color="primary">Seleccionar archivo<input hidden type="file" accept="image/*,video/*" onChange={e => setForm({ ...form, media: e.target.files?.[0] || null })} /></Button></Box>{form.media && <Typography variant="body2" style={{ marginTop: 10 }}>{form.media.name}</Typography>}{editing?.mediaName && !form.media && <Typography variant="body2" style={{ marginTop: 10 }}>Actual: {editing.mediaName}</Typography>}</div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button><Button variant="outlined" color="primary" disabled={saving} onClick={() => save(false)}>{form.scheduledAt ? "Guardar y programar" : "Guardar borrador"}</Button><Button variant="contained" color="primary" disabled={saving} onClick={() => save(true)}>Publicar ahora</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Vista preliminar · {preview?.title}</DialogTitle>
        <DialogContent>
          {preview && (preview.mediaUrl
            ? (String(preview.mimeType).startsWith("video/") ? <video className={classes.preview} src={preview.mediaUrl} controls /> : <img className={classes.preview} src={preview.mediaUrl} alt={preview.title} />)
            : <div className={classes.phonePreview} style={{ background: preview.backgroundColor || "#075E54" }}><Typography variant="h4">{preview.text}</Typography></div>)}
          {preview?.mediaUrl && preview?.text && <Typography variant="h6" align="center" style={{ marginTop: 14 }}>{preview.text}</Typography>}
          {preview?.views?.length > 0 && <Box className={classes.viewsList}><Typography variant="subtitle2">Visto por</Typography>{preview.views.map(view => <Typography key={view.id} variant="body2">• {view.viewerName || view.viewerJid} · {formatDate(view.viewedAt)}</Typography>)}</Box>}
        </DialogContent>
        <DialogActions>{preview?.mediaUrl && <Button component="a" href={preview.mediaUrl} target="_blank" startIcon={<GetAppIcon />}>Descargar</Button>}<Button color="primary" onClick={() => setPreview(null)}>Cerrar</Button></DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default WhatsAppStatuses;
